import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { HiMagnifyingGlass } from "react-icons/hi2";
import { MdChevronLeft, MdChevronRight, MdFirstPage, MdLastPage } from "react-icons/md";
import i18n from "src/i18n";

export interface DataMgmtSearchFieldProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  /** Optional min width for the field (default matches document categories). */
  minWidth?: number;
  maxWidth?: number;
}

/**
 * Catalog-style search: magnifier inside the input (same pattern as Document Categories).
 */
export const DataMgmtSearchField: React.FC<DataMgmtSearchFieldProps> = ({
  id,
  value,
  onChange,
  placeholder,
  minWidth = 160,
  maxWidth = 480,
}) => {
  const rtlList = i18n.language === "ar";

  return (
    <div
      className="services-catalog-search-wrap position-relative flex-grow-1 doc-categories-search-wrap"
      style={{ minWidth, maxWidth }}
    >
      <HiMagnifyingGlass
        className="position-absolute text-muted"
        size={16}
        style={{
          ...(rtlList ? { right: 10, left: "auto" } : { left: 10, right: "auto" }),
          top: "50%",
          transform: "translateY(-50%)",
          pointerEvents: "none",
        }}
        aria-hidden
      />
      <input
        id={id}
        type="search"
        className="form-control form-control-sm border bg-white services-catalog-search-input"
        style={{
          height: 32,
          borderRadius: 8,
          ...(rtlList ? { paddingRight: 34, paddingLeft: 12 } : { paddingLeft: 34, paddingRight: 12 }),
        }}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={placeholder}
        dir={rtlList ? "rtl" : "ltr"}
      />
    </div>
  );
};

export interface DataMgmtCatalogPaginationProps {
  /** Current page (1-based). */
  page: number;
  totalPages: number;
  /** Total items (used to disable nav when empty). */
  totalFiltered: number;
  busy?: boolean;
  onPageChange: (page: number) => void;
}

/**
 * Document-categories style pagination: Previous / numbered pages / Next + meta line.
 */
export const DataMgmtCatalogPagination: React.FC<DataMgmtCatalogPaginationProps> = ({
  page,
  totalPages,
  totalFiltered,
  busy = false,
  onPageChange,
}) => {
  const { t } = useTranslation();

  const hasCatalogPages = totalFiltered > 0;

  // Past this many pages, clicking through the numbered/ellipsis pager to
  // reach a distant page (e.g. page 30 of 40) takes several clicks — a
  // type-a-number jump box is worth the extra control at that point, but
  // would just be clutter on a 3-4 page table where every page is already
  // one click away.
  const showJumpToPage = totalPages > 10;
  const [jumpValue, setJumpValue] = useState(String(page));
  useEffect(() => { setJumpValue(String(page)); }, [page]);
  const commitJump = () => {
    const n = Math.round(Number(jumpValue));
    if (Number.isFinite(n) && n >= 1 && n <= totalPages) {
      onPageChange(n);
    } else {
      setJumpValue(String(page));
    }
  };

  const visiblePaginationPages = useMemo((): (number | "ellipsis")[] => {
    const total = Math.max(0, totalPages);
    if (total <= 0) return [];
    const cur = Math.min(Math.max(1, page), total);
    const delta = 2;
    const range: number[] = [];
    for (let i = 1; i <= total; i += 1) {
      if (i === 1 || i === total || (i >= cur - delta && i <= cur + delta)) {
        range.push(i);
      }
    }
    const result: (number | "ellipsis")[] = [];
    let prev: number | undefined;
    for (const i of range) {
      if (prev !== undefined && i - prev > 1) {
        result.push("ellipsis");
      }
      result.push(i);
      prev = i;
    }
    return result;
  }, [page, totalPages]);

  return (
    <div className="doc-categories-pagination-wrap data-mgmt-pagination-bar">
      <nav
        className="doc-categories-pagination-nav d-flex flex-wrap align-items-center justify-content-center gap-1"
        aria-label={String(
          t("document_categories.pageIndicator", {
            page,
            totalPages,
            defaultValue: `Page ${page} / ${totalPages}`,
          }),
        )}
      >
        <button
          type="button"
          className="services-catalog-page-pill doc-categories-pagination-pill"
          disabled={!hasCatalogPages || page <= 1 || busy}
          onClick={() => onPageChange(1)}
          title={String(t("common.first", { defaultValue: "First page" }))}
          aria-label={String(t("common.first", { defaultValue: "First page" }))}
        >
          <MdFirstPage size={18} aria-hidden />
        </button>
        <button
          type="button"
          className="services-catalog-page-pill doc-categories-pagination-pill"
          disabled={!hasCatalogPages || page <= 1 || busy}
          onClick={() => onPageChange(Math.max(1, page - 1))}
        >
          <MdChevronLeft size={18} aria-hidden />
          <span>{String(t("common.previous", { defaultValue: "Previous" }))}</span>
        </button>
        {visiblePaginationPages.map((item, idx) =>
          item === "ellipsis" ? (
            <span key={`ellipsis-${idx}`} className="services-catalog-page-ellipsis px-1" aria-hidden>
              …
            </span>
          ) : (
            <button
              key={item}
              type="button"
              className={`services-catalog-page-num doc-categories-pagination-num${page === item ? " is-active" : ""}`}
              disabled={busy}
              onClick={() => onPageChange(item)}
              aria-current={page === item ? "page" : undefined}
            >
              {item}
            </button>
          ),
        )}
        <button
          type="button"
          className="services-catalog-page-pill doc-categories-pagination-pill"
          disabled={!hasCatalogPages || page >= totalPages || busy}
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
        >
          <span>{String(t("common.next", { defaultValue: "Next" }))}</span>
          <MdChevronRight size={18} aria-hidden />
        </button>
        <button
          type="button"
          className="services-catalog-page-pill doc-categories-pagination-pill"
          disabled={!hasCatalogPages || page >= totalPages || busy}
          onClick={() => onPageChange(totalPages)}
          title={String(t("common.last", { defaultValue: "Last page" }))}
          aria-label={String(t("common.last", { defaultValue: "Last page" }))}
        >
          <MdLastPage size={18} aria-hidden />
        </button>
      </nav>
      {showJumpToPage ? (
        <span className="doc-categories-pagination-meta text-muted small d-inline-flex align-items-center gap-1">
          {String(t("document_categories.jumpToPagePrefix", { defaultValue: "Page" }))}
          <input
            type="number"
            min={1}
            max={totalPages}
            value={jumpValue}
            disabled={busy}
            onChange={(e) => setJumpValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") commitJump(); }}
            onBlur={commitJump}
            aria-label={String(t("document_categories.jumpToPageLabel", { totalPages, defaultValue: `Jump to page, 1 to ${totalPages}` }))}
            style={{ width: 48, padding: "2px 4px", borderRadius: 6, border: "1px solid #E5E7EB", fontSize: 12, textAlign: "center" }}
          />
          {String(t("document_categories.jumpToPageSuffix", { totalPages, defaultValue: `of ${totalPages}` }))}
        </span>
      ) : (
        <span className="doc-categories-pagination-meta text-muted small">
          {t("document_categories.pageIndicator", {
            page,
            totalPages,
            defaultValue: `Page ${page} / ${totalPages}`,
          })}
        </span>
      )}
    </div>
  );
};
